/**
 * Functional utility functions
 * Pure functions for common operations
 * No dependencies
 */

/**
 * Creates a debounced version of a function that delays execution
 * until after `wait` milliseconds have elapsed since the last call.
 * @param {Function} fn - Function to debounce
 * @param {number} wait - Milliseconds to wait before executing
 * @param {{maxWait?: number}} [options] - `maxWait` caps how long calls can keep
 *   deferring execution: once armed, the function runs after `maxWait` ms even if
 *   calls never stop arriving. 0 (default) keeps the plain debounce behaviour.
 * @returns {Function} Debounced function
 *
 * @example
 * const debouncedSearch = debounce(search, 300);
 * input.addEventListener('input', debouncedSearch);
 *
 * @example
 * // Autosave while typing: 3s after a pause, but never more than 20s apart
 * const autoSave = debounce(save, 3000, { maxWait: 20000 });
 */
export function debounce(fn, wait, { maxWait = 0 } = {}) {
    let t = null;
    let maxT = null;
    let lastArgs = null;
    let lastThis = null;

    const clearPending = () => {
        if (t) {
            clearTimeout(t);
            t = null;
        }
        if (maxT) {
            clearTimeout(maxT);
            maxT = null;
        }
    };

    const invoke = () => {
        if (!lastArgs) return undefined;
        const args = lastArgs;
        const context = lastThis;
        lastArgs = null;
        lastThis = null;
        clearPending();
        return fn.apply(context, args);
    };

    function debounced(...args) {
        lastArgs = args;
        lastThis = this;
        if (t) clearTimeout(t);
        t = setTimeout(() => {
            invoke();
        }, wait);
        // Arm the ceiling only on the first call of a burst; re-arming it on every
        // call would make it behave exactly like the regular debounce timer.
        if (maxWait > 0 && !maxT) {
            maxT = setTimeout(() => {
                maxT = null;
                invoke();
            }, maxWait);
        }
    }

    debounced.cancel = () => {
        clearPending();
        lastArgs = null;
        lastThis = null;
    };

    debounced.flush = () => invoke();
    debounced.pending = () => t !== null || maxT !== null;

    return debounced;
}

/**
 * Toggle an item in a Set - adds if `on` is true, removes if false
 * @param {Set} setObj - The Set to modify
 * @param {*} val - Value to add or remove
 * @param {boolean} on - Whether to add (true) or remove (false)
 *
 * @example
 * const selected = new Set();
 * toggleSet(selected, 'item1', true);  // adds 'item1'
 * toggleSet(selected, 'item1', false); // removes 'item1'
 */
export function toggleSet(setObj, val, on) {
    if (on) setObj.add(val);
    else setObj.delete(val);
}
